import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "../Property/GalleryModal.css";

const GalleryModal = ({ isOpen, images, propertyName, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageContainerRef = useRef(null);
  const thumbnailsRef = useRef(null);

  const handleNextImage = useCallback(() => {
    setImageLoaded(false);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  const handlePrevImage = useCallback(() => {
    setImageLoaded(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
  }, [images.length]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowRight") {
        handleNextImage();
      } else if (e.key === "ArrowLeft") {
        handlePrevImage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleNextImage, handlePrevImage, onClose]);

  // Auto-scroll active thumbnail into view
  useEffect(() => {
    if (thumbnailsRef.current && isOpen) {
      const activeThumbnail = thumbnailsRef.current.children[currentIndex];
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [currentIndex, isOpen]);

  // Reset to first image when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setImageLoaded(false);
    }
  }, [isOpen]);

  const handleThumbnailClick = (index) => {
    setImageLoaded(false);
    setCurrentIndex(index);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not the modal
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="gallery-backdrop"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        className="gallery-modal-container"
        role="dialog"
        aria-modal="true"
        aria-label={`Gallery for ${propertyName}`}
      >
        {/* Close Button */}
        <button
          className="gallery-close-btn"
          onClick={onClose}
          aria-label="Close gallery"
          title="Close (ESC)"
        >
          <FaTimes />
        </button>

        {/* Main Image Display */}
        <div className="gallery-main-section">
          <div className="gallery-image-wrapper" ref={imageContainerRef}>
            {images[currentIndex] && (
              <img
                src={images[currentIndex]}
                alt={`${propertyName} - ${currentIndex + 1} of ${images.length}`}
                className="gallery-main-image"
                onLoad={handleImageLoad}
                style={{
                  opacity: imageLoaded ? 1 : 0,
                  transition: "opacity 300ms ease",
                }}
              />
            )}
          </div>

          {/* Image Counter */}
          <div className="gallery-counter" aria-live="polite">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                className="gallery-nav-btn gallery-prev-btn"
                onClick={handlePrevImage}
                aria-label="Previous image"
                title="Previous (← arrow)"
              >
                <FaChevronLeft />
              </button>
              <button
                className="gallery-nav-btn gallery-next-btn"
                onClick={handleNextImage}
                aria-label="Next image"
                title="Next (→ arrow)"
              >
                <FaChevronRight />
              </button>
            </>
          )}
        </div>

        {/* Thumbnails Section */}
        {images.length > 1 && (
          <div className="gallery-thumbnails" ref={thumbnailsRef}>
            {images.map((image, index) => (
              <button
                key={index}
                className={`gallery-thumbnail ${
                  index === currentIndex ? "active" : ""
                }`}
                onClick={() => handleThumbnailClick(index)}
                aria-label={`View image ${index + 1} of ${images.length}`}
                aria-pressed={index === currentIndex}
                aria-current={index === currentIndex ? "true" : "false"}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}

        {/* Image Info */}
        <div className="gallery-info">
          <p className="gallery-property-name">{propertyName}</p>
          <p className="gallery-help-text">
            Use arrow keys to navigate • ESC to close
          </p>
        </div>
      </div>
    </>,
    document.body,
  );
};

GalleryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  propertyName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

GalleryModal.defaultProps = {
  propertyName: "Property",
};

export default GalleryModal;
